import { defineComponent as _defineComponent } from 'vue'
import { withModifiers as _withModifiers, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "relative sm:space-y-6 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)] flex flex-col" }
const _hoisted_2 = { class: "sm:pt-6 pt-4 sm:px-0 text-sm text-slate-500 dark:text-slate-400" }
const _hoisted_3 = { class: "text-slate-800 dark:text-slate-200 font-medium" }
const _hoisted_4 = {
  key: 0,
  class: "flex items-center justify-center py-20"
}
const _hoisted_5 = {
  key: 1,
  class: "bg-red-50 text-red-600 p-4 rounded-xl border border-red-200 sm:mx-0 mx-3"
}
const _hoisted_6 = {
  key: 2,
  class: "flex-1 flex flex-col min-h-[90vh] sm:bg-white dark:bg-slate-800/80 sm:rounded-2xl sm:shadow-lg sm:border sm:border-slate-200/50 dark:border-slate-700/50 sm:overflow-hidden"
}
const _hoisted_7 = { class: "flex items-center justify-between sm:px-6 py-3 border-b border-slate-200 dark:border-slate-700" }
const _hoisted_8 = { class: "text-lg font-semibold text-slate-800 dark:text-slate-200 truncate" }
const _hoisted_9 = { class: "sm:flex-1 sm:relative sm:min-h-0" }
const _hoisted_10 = {
  key: 0,
  class: "absolute inset-0 flex items-center justify-center bg-white dark:bg-slate-800/80 z-10"
}
const _hoisted_11 = ["src"]

import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-vue-next'
import { materialService } from '@/services/material.service'
import type { Material } from '@/config/types/material'
import Modal from '@/components/layout/Modal.vue'
import { useAuthStore } from '@/stores/auth'


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminOnlineFormView',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const material = ref<Material | null>(null)
const loading = ref(true)
const error = ref('')
const showLeaveConfirm = ref(false)
const iframeLoading = ref(true)

const backLink = computed(() =>
    route.name === 'employee-view-online-form' ? '/employee-materials' : '/admin/materials'
)

const iframeSrc = computed(() => {
    if (!material.value?.url_link) return ''
    const url = new URL(material.value.url_link)
    url.searchParams.set('material_id', String(material.value.id))
    url.searchParams.set('source', 'planet_home_sp')
    url.searchParams.set('user_id', String(authStore.currentUser?.id || ''))
    return url.toString()
})

const confirmLeave = () => {
    showLeaveConfirm.value = true
}

const leave = () => {
    showLeaveConfirm.value = false
    router.push(backLink.value)
}

const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault()
    e.returnValue = ''
}

onMounted(async () => {
    try {
        const id = Number(route.params.id)
        material.value = await materialService.fetchById(id)
        window.addEventListener('beforeunload', handleBeforeUnload)
    } catch (err: any) {
        error.value = err.message || 'Failed to load online form'
    } finally {
        loading.value = false
    }
})

onUnmounted(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("nav", _hoisted_2, [
      _createElementVNode("a", {
        href: "#",
        onClick: _withModifiers(confirmLeave, ["prevent"]),
        class: "hover:text-primary transition-colors"
      }, " Materials "),
      _createVNode(_unref(ChevronRight), { class: "mx-1 h-3.5 w-3.5 inline-block align-middle text-slate-400" }),
      _createElementVNode("span", _hoisted_3, _toDisplayString(material.value?.title || 'Online Form'), 1)
    ]),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(_unref(Loader2), { class: "w-8 h-8 animate-spin text-primary" })
        ]))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(error.value), 1))
        : (material.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("h1", _hoisted_8, _toDisplayString(material.value.title), 1),
                _createElementVNode("button", {
                  onClick: confirmLeave,
                  class: "flex items-center px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0 ml-4"
                }, [
                  _createVNode(_unref(ChevronLeft), { class: "w-5 h-5 text-primary" }),
                  _cache[3] || (_cache[3] = _createTextVNode(" Back to Materials ", -1))
                ])
              ]),
              _createElementVNode("div", _hoisted_9, [
                (iframeLoading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [...(_cache[4] || (_cache[4] = [
                      _createElementVNode("div", { class: "text-center" }, [
                        _createElementVNode("div", { class: "w-8 h-8 rounded-full border-4 border-gray-200 border-t-primary animate-spin mx-auto" }),
                        _createElementVNode("p", { class: "text-sm text-slate-500 dark:text-slate-400 mt-3" }, "Loading form...")
                      ], -1)
                    ]))]))
                  : _createCommentVNode("", true),
                _createElementVNode("iframe", {
                  src: iframeSrc.value,
                  class: "w-full sm:absolute sm:inset-0 sm:w-full sm:h-full min-h-[80vh] sm:min-h-0",
                  frameborder: "0",
                  allowfullscreen: "",
                  onLoad: _cache[0] || (_cache[0] = ($event: any) => (iframeLoading.value = false))
                }, null, 40, _hoisted_11)
              ])
            ]))
          : _createCommentVNode("", true),
    _createVNode(Modal, {
      modelValue: showLeaveConfirm.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showLeaveConfirm).value = $event)),
      title: "Leave Online Form?"
    }, {
      footer: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (showLeaveConfirm.value = false))
        }, " Cancel "),
        _createElementVNode("button", {
          type: "button",
          class: "flex justify-center ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-success hover:bg-success/90 shadow-sm hover:shadow-md transition-all duration-200",
          onClick: leave
        }, " Yes, go back ")
      ]),
      default: _withCtx(() => [
        _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-sm text-secondary-text/80 dark:text-secondary-dark-text/80" }, " Are you sure you want to go back? Any unsaved changes will not be saved. ", -1))
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})